import { Lesson, ModuleDraft } from '../../../../../../../../components/admin3/newProgram/masterclass/types';

/**
 * Client-side comparison of two editor snapshots (the current draft vs an
 * older version in the preview banner). The publish flow uses the server diff
 * (docs/API-course-versioning.md section 3) instead. Lessons are matched by
 * their template id (`sourceId`) so the comparison survives
 * mapTemplateToModules minting fresh local ids; lessons created in the editor
 * and never saved fall back to their local id.
 */

export interface LessonRef {
  key: string;
  title: string;
}

export interface SnapshotDiff {
  added: LessonRef[];
  removed: LessonRef[];
  /** Lesson title or content changed. */
  reworded: LessonRef[];
  /** Quiz questions added, removed or rewritten. */
  quiz: LessonRef[];
  /** Case studies / live scenario changed or switched on/off. */
  sections: LessonRef[];
  /** Resources or feedback questions changed (no learner policy needed). */
  other: LessonRef[];
  /** Module-level changes: added, removed or renamed modules. */
  modules: string[];
}

export const lessonKey = (lesson: Lesson): string => lesson.sourceId || lesson.id;

const quizSignature = (lesson: Lesson) =>
  JSON.stringify(
    (lesson.multipleChoiceQuestions || []).map((q) => ({
      prompt: q.prompt,
      options: (q.options || []).map((o) => o.label),
      correct: q.correctOptionId ?? null
    }))
  );

const sectionsSignature = (lesson: Lesson) =>
  JSON.stringify({
    caseStudies: lesson.elliott?.caseStudies ?? null,
    rubric: lesson.elliott?.simulationRubric ?? null,
    caseStudiesEnabled: lesson.elliott?.caseStudiesEnabled ?? true,
    simulationEnabled: lesson.elliott?.simulationEnabled ?? true,
    quizEnabled: lesson.elliott?.quizEnabled ?? true
  });

const otherSignature = (lesson: Lesson) =>
  JSON.stringify({
    materials: (lesson.materials || []).map((m) => m.articleId || m.id),
    feedback: (lesson.feedbackQuestions || []).map((q) => q.prompt)
  });

const moduleKey = (m: ModuleDraft) => m.sourceId || m.id;

function indexLessons(modules: ModuleDraft[]): Map<string, Lesson> {
  const map = new Map<string, Lesson>();
  modules.forEach((m) => m.lessons.forEach((l) => map.set(lessonKey(l), l)));
  return map;
}

export function diffCourse(from: ModuleDraft[], to: ModuleDraft[]): SnapshotDiff {
  const a = indexLessons(from);
  const b = indexLessons(to);
  const diff: SnapshotDiff = { added: [], removed: [], reworded: [], quiz: [], sections: [], other: [], modules: [] };

  const fromModules = new Map(from.map((m) => [moduleKey(m), m]));
  const toModules = new Map(to.map((m) => [moduleKey(m), m]));
  toModules.forEach((m, key) => {
    const prev = fromModules.get(key);
    if (!prev) diff.modules.push(`Module added: “${m.title}”`);
    else if (prev.title !== m.title) diff.modules.push(`Module renamed: “${prev.title}” → “${m.title}”`);
  });
  fromModules.forEach((m, key) => {
    if (!toModules.has(key)) diff.modules.push(`Module removed: “${m.title}”`);
  });

  b.forEach((lesson, key) => {
    const prev = a.get(key);
    if (!prev) {
      diff.added.push({ key, title: lesson.title });
      return;
    }
    const ref = { key, title: lesson.title };
    if (prev.title !== lesson.title || (prev.description || '') !== (lesson.description || '')) diff.reworded.push(ref);
    if (quizSignature(prev) !== quizSignature(lesson)) diff.quiz.push(ref);
    if (sectionsSignature(prev) !== sectionsSignature(lesson)) diff.sections.push(ref);
    if (otherSignature(prev) !== otherSignature(lesson)) diff.other.push(ref);
  });
  a.forEach((lesson, key) => {
    if (!b.has(key)) diff.removed.push({ key, title: lesson.title });
  });
  return diff;
}

export const diffHasChanges = (d: SnapshotDiff | null): boolean =>
  Boolean(
    d &&
      d.added.length +
        d.removed.length +
        d.reworded.length +
        d.quiz.length +
        d.sections.length +
        d.other.length +
        d.modules.length >
        0
  );

export const quoteTitles = (refs: LessonRef[]): string => refs.map((r) => `“${r.title}”`).join(', ');

export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
